import React from 'react';

import { OverflowFadeText } from '@/components/layout/OverflowFadeText';
import { AccountPlanBadge } from '@/components/ui/account-plan-badge';
import { useAuth } from '@/hooks/use-auth';
import { useSubscription } from '@/hooks/useSubscription';
import { getDisplayName } from '@/lib/format-name';
import { storeAccountIdentityLabel, storeAccountShopDomain } from '@/lib/identity/storeAccount';
import { cn } from '@/lib/utils';

interface UserMenuHeaderProps {
  className?: string;
}

export const UserMenuHeader: React.FC<UserMenuHeaderProps> = ({ className }) => {
  const { user } = useAuth();
  const { data: subscription } = useSubscription();
  const tier = subscription?.plan ?? 'free';

  if (!user) return null;

  const storeLabel = storeAccountIdentityLabel(user);
  // The name line already says "Store account"; the subline is just the shop so
  // it fits the narrow menu.
  const subline = storeLabel ? (storeAccountShopDomain(user) ?? 'Shopify store') : user.email;

  const displayName = getDisplayName(user, storeLabel ? 'Store account' : 'User');
  return (
    <div className={cn('border-b border-border/40 px-3 py-2.5 text-left', className)}>
      <div className="flex min-w-0 items-center gap-2">
        <OverflowFadeText
          className="block min-w-0 flex-1 text-caption font-semibold text-foreground"
          title={displayName}
        >
          {displayName}
        </OverflowFadeText>
        <AccountPlanBadge plan={tier} />
      </div>
      <OverflowFadeText
        className="mt-0.5 block w-full text-detail text-muted-foreground"
        title={subline ?? undefined}
      >
        {subline}
      </OverflowFadeText>
    </div>
  );
};
